import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { getServiceStrings } from './i18n'
import { API_BASE } from './apiBase'
import { refreshAccessToken } from './refreshSession'
import { clearSessionAndRedirectToLogin, getAccessToken } from './session'

export { API_BASE } from './apiBase'

export class ApiError extends Error {
  status: number
  fieldErrors: Record<string, string>
  code?: string
  details?: unknown

  constructor(
    status: number,
    message: string,
    fieldErrors: Record<string, string> = {},
    code?: string,
    details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.fieldErrors = fieldErrors
    this.code = code
    this.details = details
  }
}

/**
 * Read the backend's machine-readable error `code` (e.g. `MEETING_TITLE_TAKEN`)
 * from a NestJS error response body, when present.
 */
function getErrorCode(data: unknown): string | undefined {
  const rawCode = (data as { code?: unknown } | null)?.code
  return typeof rawCode === 'string' ? rawCode : undefined
}

function extractFieldErrors(message: unknown): Record<string, string> {
  if (!Array.isArray(message)) return {}

  const fields: Record<string, string> = {}
  const knownFields = ['email', 'password', 'confirmPassword', 'name', 'role'] as const

  for (const raw of message) {
    if (typeof raw !== 'string') continue
    const field = knownFields.find((name) => raw.toLowerCase().includes(name.toLowerCase()))
    if (field && !fields[field]) fields[field] = raw
  }

  return fields
}

function getErrorMessage(data: unknown, fallback: string): string {
  const rawMessage = (data as { message?: unknown } | null)?.message

  if (typeof rawMessage === 'string') return rawMessage
  if (Array.isArray(rawMessage)) return rawMessage.join(', ')

  return fallback
}

export function toApiError(error: unknown, fallbackMessage: string): ApiError {
  if (error instanceof ApiError) return error

  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0
    const data = error.response?.data

    return new ApiError(
      status,
      status === 0 ? getServiceStrings().networkError : getErrorMessage(data, fallbackMessage),
      extractFieldErrors((data as { message?: unknown } | null)?.message),
      getErrorCode(data),
      (data as { details?: unknown } | null)?.details,
    )
  }

  return new ApiError(0, fallbackMessage)
}

function rejectAsUnauthorized(): Promise<never> {
  clearSessionAndRedirectToLogin('server_rejected_token')
  return Promise.reject(new ApiError(401, getServiceStrings().authRequired))
}

function attachAccessToken(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const accessToken = getAccessToken()

  if (!accessToken) {
    throw new ApiError(401, getServiceStrings().authRequired)
  }

  config.headers.set('Authorization', `Bearer ${accessToken}`)
  return config
}

function handleApiError(error: AxiosError | unknown): Promise<never> {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    return rejectAsUnauthorized()
  }

  return Promise.reject(error)
}

/** Marks a request that has already been retried, so a loop cannot form. */
type RetriableConfig = InternalAxiosRequestConfig & { _retriedAfterRefresh?: boolean }

/**
 * On a 401, spend the refresh token once and replay the request.
 *
 * The access token is short-lived now, so a 401 in the middle of a meeting is
 * the expected way a session continues rather than a sign that it is over. Only
 * a refresh that fails ends it (BRAI-422).
 */
async function retryAfterRefresh(error: AxiosError | unknown): Promise<never> {
  if (!axios.isAxiosError(error) || error.response?.status !== 401 || !error.config) {
    return Promise.reject(error)
  }

  const config = error.config as RetriableConfig
  if (config._retriedAfterRefresh) {
    // The replay 401'd too: the new token is no better than the old one, so
    // this is a real rejection rather than an expiry.
    clearSessionAndRedirectToLogin('server_rejected_token')
    return Promise.reject(new ApiError(401, getServiceStrings().authRequired))
  }

  const accessToken = await refreshAccessToken()
  if (!accessToken) {
    clearSessionAndRedirectToLogin('refresh_failed')
    return Promise.reject(new ApiError(401, getServiceStrings().authRequired))
  }

  config._retriedAfterRefresh = true
  config.headers.set('Authorization', `Bearer ${accessToken}`)
  return apiClient.request(config) as Promise<never>
}

export const publicApiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
})

export const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use(
  // A request with no token to attach is refused, but the session is NOT ended
  // here. This path fires whenever the token merely could not be read, and
  // ending the session deletes the token — turning a momentary storage failure
  // into a real sign-out, mid-meeting, with no way back (BRAI-422). Only the
  // server saying the token is bad ends a session now.
  (config) => attachAccessToken(config),
  (error) => Promise.reject(error)
)

publicApiClient.interceptors.response.use((response) => response, handleApiError)
apiClient.interceptors.response.use((response) => response, retryAfterRefresh)
